import { type RefObject, useEffect, useRef } from 'react';

/**
 * Drives the transcript's top fade from the scroller's position: fully
 * transparent at `scrollTop` 0, ramping to full strength over the fade's own
 * height. A tall fade (the session panel's `h-16`) is deeper than the content's
 * top padding, so a static fade washes out the first message while it still
 * sits at rest under the header.
 *
 * Writes `style.opacity` directly, like `useBottomFade`, so scrolling never
 * re-renders the chat. With no `scrollRef` the fade stays static.
 */
export const useScrollAwareTopFade = (scrollRef: RefObject<HTMLElement> | undefined) => {
  const topFadeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const scroller = scrollRef?.current;
    const fade = topFadeRef.current;
    if (!scroller || !fade) return;

    const update = () => {
      const rampPx = fade.offsetHeight || 1;
      fade.style.opacity = String(Math.min(1, Math.max(0, scroller.scrollTop / rampPx)));
    };

    update();
    scroller.addEventListener('scroll', update, { passive: true });
    return () => scroller.removeEventListener('scroll', update);
  }, [scrollRef]);

  return topFadeRef;
};
